import { useState } from "react";
import { AuthenticatedMediaImage } from "@/components/common/AuthenticatedMediaImage";
import type { Attachment } from "@sd/types/ticket";
import { AttachmentFullPreviewDialog } from "@sd/components/tickets/AttachmentViewer";
import { downloadAttachmentFile } from "@sd/lib/attachmentMediaUrl";
import {
  attachmentKindLabel,
  attachmentMediaPaths,
  formatAttachmentSize,
  looksLikeDocAttachment,
  looksLikeImageAttachment,
  looksLikePdfAttachment,
  looksLikeSpreadsheetAttachment,
  looksLikeVideoAttachment,
  looksLikeZipAttachment,
} from "@sd/lib/ticketDetailAttachments";
import { formatDetailDateTime } from "@sd/lib/ticketDetailDisplay";
import {
  Download,
  Archive,
  File,
  FileImage,
  FileSpreadsheet,
  FileText,
  FileVideo,
  Loader2,
  Play,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { notify } from "@/components/common/Notification";

type Props = {
  attachments: Attachment[];
  loading: boolean;
  resolveUserLabel: (raw: string | undefined | null) => string;
};

function canOpenPreview(file: Attachment): boolean {
  return (
    looksLikeImageAttachment(file.name, file.type) ||
    looksLikeVideoAttachment(file.name, file.type) ||
    looksLikePdfAttachment(file.name, file.type)
  );
}

function fileKindIcon(file: Attachment) {
  if (looksLikeImageAttachment(file.name, file.type)) {
    return FileImage;
  }
  if (looksLikeVideoAttachment(file.name, file.type)) {
    return FileVideo;
  }
  if (looksLikePdfAttachment(file.name, file.type)) {
    return FileText;
  }
  if (looksLikeDocAttachment(file.name, file.type)) {
    return FileText;
  }
  if (looksLikeZipAttachment(file.name, file.type)) {
    return Archive;
  }
  if (looksLikeSpreadsheetAttachment(file.name, file.type)) {
    return FileSpreadsheet;
  }
  return File;
}

export function TicketDetailFilesSection({
  attachments,
  loading,
  resolveUserLabel,
}: Props) {
  const [previewAttachment, setPreviewAttachment] =
    useState<Attachment | null>(null);
  const [previewZoom, setPreviewZoom] = useState(1);

  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const openPreview = (file: Attachment) => {
    setPreviewZoom(1);
    setPreviewAttachment(file);
  };

  const downloadFile = async (file: Attachment) => {
    const { filePath, fallbackPaths } = attachmentMediaPaths(file);
    setDownloadingId(file.id);
    try {
      const ok = await downloadAttachmentFile(
        [filePath, ...fallbackPaths, file.url, file.mediaUrl ?? ""],
        file.name
      );
      if (!ok) {
        notify.error("Download failed", "Could not download this file.");
      }
    } finally {
      setDownloadingId(null);
    }
  };

  if (!loading && attachments.length === 0) {
    return null;
  }

  return (
    <>
      <section className="mt-6 overflow-hidden rounded-xl border border-border bg-card shadow-sm">
        <div className="flex items-center justify-between border-b border-border/80 bg-muted/30 px-4 py-2.5">
          <h3 className="text-sm font-semibold text-foreground">Files</h3>
          {!loading && attachments.length > 0 ? (
            <span className="text-xs text-muted-foreground">
              {attachments.length} file{attachments.length === 1 ? "" : "s"}
            </span>
          ) : null}
        </div>

        {loading ? (
          <div className="flex items-center gap-2 px-4 py-8 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading files…
          </div>
        ) : (
          <ul className="divide-y divide-border/60">
            {attachments.map(file => {
              const KindIcon = fileKindIcon(file);
              const kind = attachmentKindLabel(file.name, file.type);
              const sizeLabel = formatAttachmentSize(file.size);
              const uploader = resolveUserLabel(file.uploadedBy);
              const uploadedAt = formatDetailDateTime(file.uploadedAt);
              const isImage = looksLikeImageAttachment(file.name, file.type);
              const isVideo = looksLikeVideoAttachment(file.name, file.type);
              const isPdf = looksLikePdfAttachment(file.name, file.type);
              const isDoc = looksLikeDocAttachment(file.name, file.type);
              const isZip = looksLikeZipAttachment(file.name, file.type);
              const isSpreadsheet = looksLikeSpreadsheetAttachment(
                file.name,
                file.type
              );
              const showPreview = canOpenPreview(file);
              const { filePath, fallbackPaths } = attachmentMediaPaths(file);
              const isDownloading = downloadingId === file.id;

              const metaParts: string[] = [];
              if (uploader && uploader !== "—") metaParts.push(uploader);
              if (uploadedAt) metaParts.push(uploadedAt);
              if (metaParts.length === 0) metaParts.push(kind);

              return (
                <li
                  key={file.id}
                  className="flex items-start gap-4 px-4 py-3 sm:py-3.5"
                >
                  <div
                    className={cn(
                      "mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border/70 bg-muted/40",
                      isImage && "text-emerald-600",
                      isVideo && "text-violet-600",
                      isPdf && "text-red-600",
                      isDoc && "text-blue-600",
                      isZip && "text-amber-600",
                      isSpreadsheet && "text-emerald-700",
                      !isImage &&
                        !isVideo &&
                        !isPdf &&
                        !isDoc &&
                        !isZip &&
                        !isSpreadsheet &&
                        "text-muted-foreground"
                    )}
                  >
                    <KindIcon className="h-4 w-4" />
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      {showPreview ? (
                        <button
                          type="button"
                          onClick={() => openPreview(file)}
                          className="max-w-full truncate text-left text-sm font-medium text-primary hover:underline"
                          title={file.name}
                        >
                          {file.name}
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => void downloadFile(file)}
                          disabled={isDownloading}
                          className="max-w-full truncate text-left text-sm font-medium text-primary hover:underline disabled:opacity-60"
                          title={`Download ${file.name}`}
                        >
                          {file.name}
                        </button>
                      )}
                      {sizeLabel ? (
                        <span className="shrink-0 text-xs text-muted-foreground">
                          ({sizeLabel})
                        </span>
                      ) : null}
                    </div>

                    <p className="mt-1 text-xs text-muted-foreground">
                      {metaParts.join(" · ")}
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => void downloadFile(file)}
                    disabled={isDownloading}
                    className="inline-flex shrink-0 items-center gap-1 rounded-md border border-border px-2 py-1 text-xs font-medium text-foreground hover:bg-muted disabled:opacity-60"
                    title={`Download ${file.name}`}
                  >
                    {isDownloading ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Download className="h-3.5 w-3.5" />
                    )}
                    Download
                  </button>
                  {showPreview && filePath ? (
                    <button
                      type="button"
                      onClick={() => openPreview(file)}
                      className="group relative hidden h-[4.5rem] w-[6.5rem] shrink-0 overflow-hidden rounded-lg border border-border/80 bg-muted/30 shadow-sm transition hover:border-primary/50 hover:shadow-md sm:block"
                      aria-label={`Preview ${file.name}`}
                    >
                      {isImage ? (
                        <AuthenticatedMediaImage
                          fileId={file.id}
                          filePath={filePath}
                          fallbackPaths={fallbackPaths}
                          alt={file.name}
                          className="h-full w-full object-cover"
                          style={{ display: "block", background: "#fff" }}
                        />
                      ) : isPdf ? (
                        <div className="flex h-full w-full flex-col items-center justify-center bg-red-50">
                          <FileText className="h-7 w-7 text-red-600" />
                          <span className="mt-1.5 text-[10px] font-semibold text-red-600">
                            PDF
                          </span>
                        </div>
                      ) : (
                        <div className="flex h-full w-full flex-col items-center justify-center bg-neutral-900 text-white">
                          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15 ring-1 ring-white/25 transition group-hover:scale-105 group-hover:bg-white/25">
                            <Play className="h-4 w-4 fill-white text-white" />
                          </div>
                          <span className="mt-1.5 text-[10px] font-medium text-white/80">
                            Video
                          </span>
                        </div>
                      )}
                      <div className="pointer-events-none absolute inset-0 bg-black/0 transition group-hover:bg-black/10" />
                    </button>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <AttachmentFullPreviewDialog
        attachment={previewAttachment}
        open={Boolean(previewAttachment)}
        onOpenChange={open => {
          if (!open) {
            setPreviewAttachment(null);
          }
        }}
        zoom={previewZoom}
        onZoomChange={setPreviewZoom}
        videoShowPlayStopBar
      />
    </>
  );
}
